/**
 * ContractKanbanCard - Card de contrato no Kanban de Contratos
 *
 * Card somente leitura (sem arrastar e soltar). O contrato muda de coluna
 * automaticamente quando o status muda no backend.
 *
 * @version 1.5.0
 * RELEASE: 2026-08-13 v1.5.0 - Card mais compacto: o bloco "Assinaturas" sai da Linha 5 e vira um
 *          indicador inline no topo (mesmo padrão do checklist do modal de tarefas: x/y + barra
 *          h-1 pastel), ocupando o lugar do badge de status (a cor/rótulo já vive no header da
 *          coluna). O número do contrato é mantido e o spinner de processamento segue ao lado dele
 * RELEASE: 2026-08-12 v1.4.0 - Data do rodapé passa a mostrar "Atualizado" (updated_at), que é a
 *          base de ordenação do Kanban; mantém fallback para os marcos do contrato
 * RELEASE: 2026-08-12 v1.3.0 - Barra de assinaturas suavizada: trilho `bg-muted/60` e indicador
 *          pastel por progresso (verde = todos assinaram, âmbar = parcial, cinza = nenhum)
 * RELEASE: 2026-08-12 v1.2.0 - Removida a faixa lateral colorida (a cor agora vive no header
 *          da coluna, igual ao Kanban de Negócios); card neutro com padding próprio
 * RELEASE: 2026-08-12 v1.1.0 - Blindagem de largura: card w-full/max-w-full/min-w-0,
 *          título com line-clamp-2 + break-words, truncate + title (tooltip nativo)
 *          em template/cliente/negócio/vendedor e whitespace-nowrap em valor/data
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, FileSignature, Briefcase } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { ContractRowActions } from '@/components/contracts/ContractRowActions';
import { CONTRACT_PROCESSING_STATUSES } from './contractKanbanColumns';
import type { KanbanContract } from '@/hooks/useContractKanbanColumn';

interface ContractKanbanCardProps {
  /** Contrato exibido */
  contract: KanbanContract;
  /** Tenant ativo (necessário para as ações) */
  tenantId: string;
  /** Clique no card (abre o negócio) */
  onClick?: () => void;
  /** Callback após ação concluída */
  onActionComplete: () => void;
}

/** Formata o número do contrato com 8 dígitos */
function formatContractNumber(num: number | null): string {
  if (!num) return '—';
  return String(num).padStart(8, '0');
}

/** Data relevante conforme o status do contrato */
function getRelevantDate(contract: KanbanContract): { label: string; value: string | null } {
  // v1.4.0: o Kanban ordena por data de alteração — exibimos a mesma referência no card
  if (contract.updated_at) return { label: 'Atualizado', value: contract.updated_at };
  if (contract.signed_at) return { label: 'Assinado', value: contract.signed_at };
  if (contract.sent_at) return { label: 'Enviado', value: contract.sent_at };
  if (contract.generated_at) return { label: 'Gerado', value: contract.generated_at };
  return { label: 'Criado', value: contract.created_at };
}

export function ContractKanbanCard({
  contract,
  tenantId,
  onClick,
  onActionComplete,
}: ContractKanbanCardProps) {
  const isProcessing = CONTRACT_PROCESSING_STATUSES.has(contract.status);
  const relevantDate = getRelevantDate(contract);

  // Progresso de assinaturas (evita divisão por zero)
  const signersCount = Number(contract.signers_count) || 0;
  const signedCount = Number(contract.signed_count) || 0;
  const signedPercent = signersCount > 0 ? Math.round((signedCount / signersCount) * 100) : 0;

  // v1.5.0: mesma escala pastel do checklist do modal de tarefas (ChecklistGroupCard)
  const signatureIndicatorClass =
    signedPercent >= 100
      ? 'bg-emerald-300'
      : signedPercent >= 34
        ? 'bg-amber-300'
        : 'bg-slate-300';

  const signatureTextClass =
    signedPercent >= 100
      ? 'text-emerald-500 dark:text-emerald-300'
      : signedPercent >= 34
        ? 'text-amber-500 dark:text-amber-300'
        : 'text-slate-400';

  return (
    <div
      className="group relative w-full max-w-full min-w-0 overflow-hidden rounded-md border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md cursor-pointer"
      onClick={onClick}
    >
      <div className="min-w-0 p-2.5 space-y-2">
        {/* Linha 1: número do contrato + progresso de assinaturas + ações.
            v1.5.0: o badge de status saiu (a cor/rótulo já vive no header da coluna) e no lugar
            entrou o indicador compacto de assinaturas, no mesmo padrão do checklist de tarefas. */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <FileSignature className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="font-mono text-[11px] text-muted-foreground truncate">
              {formatContractNumber(contract.contract_number)}
            </span>
            {/* Spinner mantido para status de fila/processamento */}
            {isProcessing && (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {signersCount > 0 && (
              <div
                className="flex w-24 items-center gap-1.5"
                title={`Assinaturas ${signedCount}/${signersCount}`}
              >
                <span className={cn('text-[10px] tabular-nums font-medium', signatureTextClass)}>
                  {signedCount}/{signersCount}
                </span>
                <Progress
                  value={signedPercent}
                  className="h-1 flex-1 bg-muted/60"
                  indicatorClassName={cn(signatureIndicatorClass, 'transition-colors')}
                />
              </div>
            )}
            <div onClick={(e) => e.stopPropagation()}>
              <ContractRowActions
                contract={contract}
                tenantId={tenantId}
                onViewDeal={() => onClick?.()}
                onActionComplete={onActionComplete}
                triggerClassName="h-6 w-6"
              />
            </div>
          </div>
        </div>


        {/* Linha 2: título do contrato / template (title = tooltip nativo com texto completo) */}
        <div className="min-w-0">
          <p
            className="text-xs font-medium leading-tight line-clamp-2 break-words"
            title={contract.title || undefined}
          >
            {contract.title}
          </p>
          {contract.template_name && (
            <p
              className="text-[10px] text-muted-foreground truncate"
              title={contract.template_name}
            >
              {contract.template_name}
            </p>
          )}
        </div>

        {/* Linha 3: cliente */}
        {contract.contact_name && (
          <div className="flex items-center gap-1.5 min-w-0">
            <SmartAvatar
              src={contract.contact_avatar}
              name={contract.contact_name}
              size="xs"
            />
            <span className="text-[11px] truncate" title={contract.contact_name}>
              {contract.contact_name}
            </span>
          </div>
        )}

        {/* Linha 4: negócio + valor */}
        {(contract.deal_title || contract.deal_value != null) && (
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-1 min-w-0">
              <Briefcase className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span
                className="text-[10px] text-muted-foreground truncate"
                title={contract.deal_title || undefined}
              >
                {contract.deal_title || '—'}
              </span>
            </div>
            {contract.deal_value != null && (
              <PriceDisplay
                amount={Number(contract.deal_value)}
                currency={contract.deal_currency || 'BRL'}
                size="sm"
                truncate
                className="shrink-0 whitespace-nowrap text-[11px] font-medium"
              />
            )}
          </div>
        )}



        {/* Linha 6: vendedor + data relevante */}
        <div className="flex items-center justify-between gap-2 pt-0.5 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {contract.seller_name ? (
              <>
                <SmartAvatar
                  src={contract.seller_avatar}
                  name={contract.seller_name}
                  size="xs"
                />
                <span
                  className="text-[10px] text-muted-foreground truncate"
                  title={contract.seller_name}
                >
                  {contract.seller_name}
                </span>
              </>
            ) : (
              <span className="text-[10px] text-muted-foreground truncate">Sem vendedor</span>
            )}
          </div>
          {relevantDate.value && (
            <span className="shrink-0 whitespace-nowrap text-[10px] text-muted-foreground">
              {relevantDate.label} {format(new Date(relevantDate.value), 'dd/MM', { locale: ptBR })}
            </span>
          )}

        </div>
      </div>
    </div>
  );
}
